import { useCallback, useEffect, useState } from 'react';
import { useMemberTeams, useTeams } from '../use-teams';
import { useWorkspaceNavigate } from '../../workspace-path';
import {
  billingDetailsSchema,
  projectRoleSchema,
  teamSchema,
  type Team,
  taskPrioritySchema,
  taskStatusSchema,
  holidayImportResultSchema,
  holidaySchema,
  leaveQuotaSchema,
  leaveTypeSchema,
  overtimeTypeSchema,
  skillCategorySchema,
  skillLevelSchema,
  skillSchema,
  personSchema,
  workScheduleAssignmentSchema,
  workScheduleTemplateSchema,
  worklogRulesSchema,
  workspaceSettingsSchema,
  type BillingDetails,
  type ProjectRole,
  type TaskPriority,
  type TaskStatus,
  type Holiday,
  type LeaveQuota,
  type LeaveType,
  type OvertimeType,
  type Skill,
  type SkillCategory,
  type SkillLevel,
  type Person,
  type WorkScheduleAssignment,
  type WorkScheduleTemplate,
  type WorklogRules,
  type WorkspaceSettings,
} from '@forecastify/shared';
import { Toaster, type SortState, type Toast } from '@forecastify/ui';
import { usePages } from '../use-pages';
import { formatCalendarDate } from '../history/history-labels';
import { useFirstLoad } from '../use-first-load';
import { useI18n, useT, type Translate } from '../../i18n';
import { useSession } from '../../session';
import { forgetAvatars, useAvatars } from '../use-avatar';
import { SkillsSection } from './skills-section';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { parseList, usePermissionSet } from '../permissions-hook';
import { SettingsDenied, SettingsShell, sectionPath } from './settings-shell';
import { DataSection } from './data-section';
import { HolidaysSection } from './holidays-section';
import {
  currencyRateDaySchema,
  currencySettingsSchema,
  formatSort,
  rateAssignmentListSchema,
  rateCardTemplateSchema,
  type CurrencyRateCell,
  type CurrencyRateDay,
  type CurrencySettings,
  type RateAssignmentList,
  type RateCardTemplate,
  type WorkspaceCurrency,
} from '@forecastify/shared';
import { SchedulesSection } from './schedules-section';
import { CurrencySection } from './currency-section';
import { RateCardSection } from './rate-card-section';
import { LeaveSection } from './leave-section';
import { OvertimeSection } from './overtime-section';
import { countryName } from '../countries';
import { useLiveInvalidation } from '../../live/use-live';
import {
  CALENDAR_TOPICS,
  LEAVE_TOPICS,
  MONEY_TOPICS,
  OVERTIME_TOPICS,
  TEAM_TOPICS,
  SKILL_TOPICS,
  SCHEDULE_TOPICS,
} from '../../live/topics';
import {
  GeneralSection,
  draftOf,
  slugLooksValid,
  type GeneralDraft,
  type SlugState,
} from './general-section';
import {
  IntegrationInstanceRoute,
  IntegrationProviderRoute,
  IntegrationsCatalogueRoute,
} from '../integrations/integrations-route';
import { useExport } from '../exports/use-export';
import { VocabularySection } from './vocabulary-section';
import { ProjectRolesSection } from './project-roles-section';
import { TeamsSection } from './teams-section';
import { BillingSection, billingDraftOf, type BillingDraft } from './billing-section';
import { WorklogSection } from './worklog-section';
import { AuditSection } from './audit-section';
import { useSaveToasts } from '../use-save-toasts';
import { MetafieldsRoute } from '../metafields/metafields-route';
import { PermissionsRoute } from '../permissions/permissions-route';
import { requestAll } from '../read-all';

/**
 * C16's settings routes — one per section, all wearing the same shell.
 *
 * ⚠️ **The shell is a WRAPPER the section routes share, not a parent route with an `<Outlet />`.**
 * `routes.tsx` is a flat table by design (*"the route table is DATA, so a test can enumerate it"*),
 * and nesting would put the sections' paths in two places: the table and a children array.
 */

let toastCounter = 0;
const nextToastId = (): string => `settings-${String((toastCounter += 1))}`;

/**
 * 26 Oct 2026 · a dialog's save, reported back: the dialog closes on `true` only, and a save that
 * landed runs `onLanded` — its success toast.
 */
async function confirmed(landing: Promise<boolean>, onLanded: () => void): Promise<boolean> {
  const landed = await landing;
  if (landed) onLanded();
  return landed;
}

function messageFor(t: Translate, code: string): string {
  if (code === 'slug_taken') return t('settings.error.slugTaken');
  if (code === 'slug_reserved') return t('settings.error.slugReserved');
  if (code === 'confirmation_mismatch') return t('settings.error.confirmMismatch');
  if (code === 'deletion_state') return t('settings.error.deletionState');
  if (code === 'conflict') return t('error.conflict');
  if (code === 'not_found') return t('error.not_found');
  /* ⚠️ C54 — the three the skill screen can receive, each answered by a different action. */
  if (code === 'name_taken') return t('settings.error.nameTaken');
  if (code === 'skill_in_use') return t('settings.error.skillInUse');
  if (code === 'last_of_its_kind') return t('settings.error.lastOfItsKind');
  return t('error.unavailable');
}

/**
 * The workspace read, shared by every section that needs it.
 *
 * ⚠️ **`not_found` from `settings.view` is the DENIED body, not the error state.** `state-rules.md`
 * keeps the two apart, and here the distinction is the whole point: nothing failed, the caller simply
 * may not look. Rendering `ScreenState`'s error would offer a Retry button that will never work.
 */
function useWorkspace(): {
  settings: WorkspaceSettings | null;
  denied: boolean;
  failure: ScreenFailure | null;
  reload: () => Promise<void>;
} {
  const session = useSession();
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [denied, setDenied] = useState(false);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session.request('/workspace').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (response.status === 404) {
      setDenied(true);
      setFailure(null);
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = workspaceSettingsSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setDenied(false);
    setFailure(null);
    setSettings(parsed.data);
  }, [session]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { settings, denied, failure, reload };
}

/** The shell around any section body, with the rail's navigation wired to real paths. */
export function Framed({
  section,
  children,
}: {
  readonly section: string;
  readonly children: React.ReactNode;
}) {
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const permissions = usePermissionSet();

  return (
    <SettingsShell
      section={section}
      permissions={permissions}
      onNavigate={(key) => {
        go(sectionPath(key));
      }}
    >
      {children}
    </SettingsShell>
  );
}

/* ── Általános ──────────────────────────────────────────────────────────────────────────────── */

export function SettingsGeneralRoute() {
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();
  const { settings, denied, failure, reload } = useWorkspace();

  const [draft, setDraft] = useState<GeneralDraft | null>(null);
  const [slugState, setSlugState] = useState<SlugState>('idle');
  const [saving, setSaving] = useState(false);

  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️ ITEM 59 · the workspace's icon.                                    │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * The whole subsystem has existed since C42 — `0061`'s `workspace_icon` target,
   * `/files/workspace-icon/:id`, and the create-workspace dialog's own upload. Only the settings
   * screen never caught up, so an icon could be set when a workspace was MADE and never after.
   *
   * ⚠️⚠️ **THE NONCE IS WHAT MAKES *"updates the switcher"* TRUE.** The path does not change when
   * the bytes do, so `useAvatars` — which caches by path — would keep serving the old picture.
   * `forgetAvatars()` clears the module cache for the SIDEBAR; the nonce re-keys this card's own
   * request. Neither alone is enough.
   */
  const [iconNonce, setIconNonce] = useState(0);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  /* 3 Nov 2026 · ⚠️ **only when there is an icon to read** — `/auth/me`'s `has_icon`, which the
     switcher already checks — or one was just uploaded here (the nonce moved before the session
     re-read). Unconditionally, every visit logged a 404 for a picture that does not exist. */
  const hasIcon =
    iconNonce > 0 ||
    session.workspaces.some((entry) => entry.id === session.workspaceId && entry.hasIcon);
  const iconPath =
    session.workspaceId === null || !hasIcon
      ? null
      : `/files/workspace-icon/${session.workspaceId}?v=${String(iconNonce)}`;
  const icons = useAvatars(iconPath === null ? [] : [iconPath]);
  const iconUrl = iconPath === null ? undefined : icons.get(iconPath);

  const uploadIcon = async (file: File): Promise<void> => {
    if (session.workspaceId === null) return;
    setUploadingIcon(true);
    const form = new FormData();
    form.append('file', file);

    const response = await session
      .request(`/files?target=workspace_icon&targetId=${session.workspaceId}`, {
        method: 'POST',
        body: form,
      })
      .catch(() => null);
    setUploadingIcon(false);

    /**
     * ┌─────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ IT SAYS WHAT WENT WRONG NOW.
     * └─────────────────────────────────────────────────────────────────────┘
     *
     * The note that stood here read: *"not fatal: the picture either changes or it does not, which
     * is the one kind of failure somebody can see for themselves without being told."* That is
     * wrong in the one case it matters. Reported after testing: *"no matter if I drag or upload
     * manually, no matter what picture, I get no error, and nothing happens"* - and the reason was
     * `scanner_unavailable`, a stopped ClamAV container. A person cannot see THAT for themselves;
     * what they see is a control that does nothing, which they then try four more times.
     *
     * ⚠️ **The same four codes the avatar upload names**, in the same words - `profile-section.
     * tsx` learned them first, and two uploads disagreeing about what a rejected file is called
     * would be worse than either message.
     */
    if (response?.ok !== true) {
      const body = (await response?.json().catch(() => null)) as
        { error?: { code?: string } } | null | undefined;
      const code = body?.error?.code;
      setIconError(
        code === 'file_too_large'
          ? t('account.avatar.tooLarge')
          : code === 'unsupported_file_type'
            ? t('account.avatar.wrongType')
            : code === 'file_infected'
              ? t('account.avatar.infected')
              : code === 'scanner_unavailable'
                ? t('account.avatar.scannerDown')
                : t('account.avatar.failed'),
      );
      return;
    }

    setIconError(null);
    forgetAvatars();
    setIconNonce((current) => current + 1);
  };
  const [iconError, setIconError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  /* ⚠️ Seeded once per loaded workspace, not on every render — otherwise every keystroke is undone
   * by the next paint. The same re-seed guard the onboarding wizard uses. */
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (settings !== null && seededFor !== settings.id + String(settings.version)) {
    setSeededFor(settings.id + String(settings.version));
    setDraft(draftOf(settings));
    setSlugState('idle');
  }

  /**
   * The slug check.
   *
   * ⚠️⚠️ **DEBOUNCED, AND THE SHAPE IS CHECKED FIRST.** Asking on every keystroke would send a
   * request per character and race the answers; asking about `del` while somebody types `delivery`
   * would also report "free" about a name they are not choosing. The shape check comes first because
   * the endpoint refuses a malformed slug with `validation_failed` — which is the right answer, and
   * not one worth a round trip.
   */
  useEffect(() => {
    if (draft === null || settings === null) return;
    if (draft.slug === settings.slug) {
      setSlugState('idle');
      return;
    }
    if (!slugLooksValid(draft.slug)) {
      setSlugState('invalid');
      return;
    }

    setSlugState('checking');
    const alive = { current: true };
    const timer = setTimeout(() => {
      void (async () => {
        const response = await session
          .request(`/workspace/slug-available?slug=${encodeURIComponent(draft.slug)}`)
          .catch(() => null);
        if (!alive.current) return;

        if (response?.ok !== true) {
          setSlugState('invalid');
          return;
        }
        /* ⚠️ 0098 · `reason` says WHY it is unavailable — `taken` or `reserved`. Anything else,
           including its absence from an older API, falls back to `taken`: the Save is blocked
           either way, so an unknown reason must not read as free. */
        const body = (await response.json()) as { available?: boolean; reason?: string };
        if (body.available === true) {
          setSlugState('free');
          return;
        }
        setSlugState(body.reason === 'reserved' ? 'reserved' : 'taken');
      })();
    }, 400);

    return () => {
      /* ⚠️ A mutable object, not a `let` — `hard-won.md` C15-3. */
      alive.current = false;
      clearTimeout(timer);
    };
  }, [session, draft, settings]);

  const save = (): void => {
    void (async () => {
      if (settings === null || draft === null) return;
      setSaving(true);
      setServerError(null);

      const response = await session
        .request('/workspace', {
          method: 'PATCH',
          body: JSON.stringify({ ...draft, version: settings.version }),
        })
        .catch(() => null);

      setSaving(false);
      if (response === null) {
        setServerError(t('error.unavailable'));
        return;
      }
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: { code?: string };
        } | null;
        setServerError(messageFor(t, body?.error?.code ?? 'unavailable'));
        return;
      }

      await reload();
      /**
       * 26 Oct 2026 · **the session's own copy of the workspace, re-read when it changed.** The
       * switcher draws the name and every address starts with the slug, both out of `/auth/me`
       * — so without this a new slug left the app on an address its list no longer knew.
       * `WorkspaceScope` moves the address bar to the new slug (`useRenamedSlugs`).
       */
      if (draft.slug !== settings.slug || draft.name !== settings.name) await session.reload();
      setToasts((current) => [
        ...current,
        { id: nextToastId(), title: t('settings.toast.saved'), variant: 'success' },
      ]);
    })();
  };

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <Framed section="general">
          {denied ? (
            <SettingsDenied />
          ) : settings === null || draft === null ? (
            <p className="text-muted-foreground p-5 text-[12.5px]">{t('state.loading')}</p>
          ) : (
            <GeneralSection
              settings={settings}
              draft={draft}
              /* ⚠️ Item 59 · the picture and its upload belong to the ROUTE — the section is
                 presentational, and putting `useSession` in it broke six of its own tests. */
              iconUrl={iconUrl}
              uploading={uploadingIcon}
              iconError={iconError}
              onIcon={(file) => {
                void uploadIcon(file);
              }}
              slugState={slugState}
              canManage={permissions.has('settings.manage')}
              saving={saving}
              serverError={serverError}
              onDraft={(patch) => {
                setDraft((current) => (current === null ? current : { ...current, ...patch }));
              }}
              onReset={() => {
                setDraft(draftOf(settings));
                setSlugState('idle');
                setServerError(null);
              }}
              onSave={save}
            />
          )}
        </Framed>
      </ScreenState>

      <Toaster
        toasts={[...toasts]}
        label={t('settings.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}

/* ── Taskok: statuses and priorities ────────────────────────────────────────────────────────── */

/**
 * ⚠️ **Against C8's endpoints, unchanged.** `/task-statuses` and `/task-priorities` already carry
 * create, update, set-default, reorder and delete-with-reassign; this route only calls them.
 *
 * ⚠️ **Every write RELOADS both lists rather than patching one in place.** A reorder renumbers every
 * position, a set-default clears somebody else's flag, and a delete-with-reassign moves task counts
 * between two rows — three operations whose effect is never confined to the row that was clicked.
 * `state-rules.md` allows an optimistic write; it does not allow one that would be wrong.
 */
export function SettingsVocabularyRoute() {
  const session = useSession();
  const t = useT();
  /* 26 Oct 2026 · the edit dialog's success toast. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();

  const [statuses, setStatuses] = useState<readonly TaskStatus[]>([]);
  const [priorities, setPriorities] = useState<readonly TaskPriority[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const [statusList, priorityList] = await Promise.all([
      session.request('/task-statuses').catch(() => null),
      session.request('/task-priorities').catch(() => null),
    ]);

    if (statusList === null || priorityList === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!statusList.ok) {
      setFailure(await failureOf(statusList));
      return;
    }

    const parsedStatuses = parseList(taskStatusSchema, await statusList.json());
    const parsedPriorities = priorityList.ok
      ? parseList(taskPrioritySchema, await priorityList.json())
      : null;
    if (parsedStatuses === null || parsedPriorities === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setStatuses(parsedStatuses);
    setPriorities(parsedPriorities);
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /** 26 Oct 2026 · resolves to whether it landed — the edit dialog closes on `true` only. */
  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setBusy(true);
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    setBusy(false);

    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      setServerError(vocabularyMessage(t, body?.error?.code ?? 'unavailable'));
      return false;
    }
    await reload();
    return true;
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="tasks">
        <VocabularySection
          loading={loading}
          statuses={statuses}
          priorities={priorities}
          canManage={permissions.has('settings.manage')}
          busy={busy}
          serverError={serverError}
          onCreateStatus={(body) => {
            return confirmed(
              write('/task-statuses', { method: 'POST', body: JSON.stringify(body) }),
              () => {
                saved(t('common.toast.created', { name: body.name }));
              },
            );
          }}
          onUpdateStatus={(id, body) => {
            return confirmed(
              /* ⚠️⚠️ **`PUT`, AND IT WAS `PATCH` — 28 Oct 2026, UAT.** `tasks.controller.ts` declares
                 `@Put(':id')`: the body is the whole row and its version. Every rename answered
                 Nest's own 404, the dialog said *not available*, and the row never changed. */
              write(`/task-statuses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
              () => {
                saved(t('common.toast.saved', { name: body.name }));
              },
            );
          }}
          onDefaultStatus={(id) => {
            /* ⚠️⚠️ **`PATCH`, AND IT WAS `PUT` — item 63's *"setting a default stops erroring"*.**
               `tasks.controller.ts` declares `@Patch(':id/default')` for both vocabularies, so
               every attempt answered 404 and the row simply never changed. A 404 from a write is
               the quietest failure there is: nothing on screen, nothing in the log a reader would
               look at, and a control that appears to do nothing. */
            void write(`/task-statuses/${id}/default`, { method: 'PATCH' });
          }}
          onReorderStatuses={(ids) => {
            /* ⚠️⚠️ **`PATCH /order`, AND IT WAS `PUT /reorder` — WRONG VERB AND WRONG PATH.**
               `tasks.controller.ts` declares `@Patch('order')`, so every drag answered 404 and the
               order silently snapped back on the next load. Found while fixing the default; the
               two vocabularies had the same pair of defects.

               ⚠️ **`/project-roles/reorder` is genuinely `PUT`** and is left alone — the two lists
               look alike and their endpoints do not, which is how this was missed. */
            void write('/task-statuses/order', { method: 'PATCH', body: JSON.stringify({ ids }) });
          }}
          onDeleteStatus={(id, reassignToId) => {
            void write(`/task-statuses/${id}`, {
              method: 'DELETE',
              body: JSON.stringify({ reassignToId }),
            });
          }}
          onCreatePriority={(body) => {
            return confirmed(
              write('/task-priorities', { method: 'POST', body: JSON.stringify(body) }),
              () => {
                saved(t('common.toast.created', { name: body.name }));
              },
            );
          }}
          onUpdatePriority={(id, body) => {
            return confirmed(
              /* ⚠️ `PUT` — the status handler above. */
              write(`/task-priorities/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
              () => {
                saved(t('common.toast.saved', { name: body.name }));
              },
            );
          }}
          onDefaultPriority={(id) => {
            /* ⚠️ Same defect, same fix — see the status handler above. */
            void write(`/task-priorities/${id}/default`, { method: 'PATCH' });
          }}
          onReorderPriorities={(ids) => {
            /* ⚠️ Same defect, same fix — see the status handler above. */
            void write('/task-priorities/order', {
              method: 'PATCH',
              body: JSON.stringify({ ids }),
            });
          }}
          onDeletePriority={(id, reassignToId) => {
            void write(`/task-priorities/${id}`, {
              method: 'DELETE',
              body: JSON.stringify({ reassignToId }),
            });
          }}
        />
      </Framed>
      {toaster}
    </ScreenState>
  );
}

/** C8's two refusals with their own codes, worded here. */
function vocabularyMessage(t: Translate, code: string): string {
  if (code === 'last_of_its_kind') return t('settings.error.lastOfItsKind');
  if (code === 'not_a_permutation') return t('settings.error.staleOrder');
  if (code === 'conflict') return t('error.conflict');
  if (code === 'not_found') return t('error.not_found');
  return t('error.unavailable');
}

/* ── Worklog szabályok ──────────────────────────────────────────────────────────────────────── */

/** ⚠️ The form C13 owed — its endpoint, guard and enforcement all shipped in C13. */
export function SettingsWorklogRoute() {
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();

  const [rules, setRules] = useState<(WorklogRules & { version: number }) | null>(null);
  const [draft, setDraft] = useState<WorklogRules | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session.request('/worklogs/rules').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const body = (await response.json()) as unknown;
    const parsed = worklogRulesSchema.safeParse(body);
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    /* ⚠️ The version rides beside the rules rather than inside `WorklogRules`, which is the shared
     * predicate type the enforcement uses — putting a wire concern in it would leak into `rules.ts`. */
    const version = (body as { version?: number }).version ?? 0;
    setFailure(null);
    setRules({ ...parsed.data, version });
    setDraft(parsed.data);
  }, [session]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <Framed section="worklog">
          {rules === null || draft === null ? (
            <p className="text-muted-foreground p-5 text-[12.5px]">{t('state.loading')}</p>
          ) : (
            <WorklogSection
              rules={rules}
              draft={draft}
              canManage={permissions.has('settings.manage')}
              saving={saving}
              serverError={serverError}
              onDraft={(patch) => {
                setDraft((current) => (current === null ? current : { ...current, ...patch }));
              }}
              onReset={() => {
                setDraft({
                  unit: rules.unit,
                  maxPerDay: rules.maxPerDay,
                  backdateDays: rules.backdateDays,
                  backdateNoLimit: rules.backdateNoLimit,
                  lockDay: rules.lockDay,
                });
                setServerError(null);
              }}
              onSave={() => {
                void (async () => {
                  setSaving(true);
                  setServerError(null);
                  const response = await session
                    .request('/worklogs/rules', {
                      method: 'PUT',
                      body: JSON.stringify({ ...draft, version: rules.version }),
                    })
                    .catch(() => null);
                  setSaving(false);

                  if (response === null) {
                    setServerError(t('error.unavailable'));
                    return;
                  }
                  if (!response.ok) {
                    const body = (await response.json().catch(() => null)) as {
                      error?: { code?: string };
                    } | null;
                    setServerError(messageFor(t, body?.error?.code ?? 'unavailable'));
                    return;
                  }
                  /* ⚠️ The reload re-seeds the draft from the saved rules, so the Save goes back
                   to disabled — the same toast General answers its own Save with. */
                  await reload();
                  setToasts((current) => [
                    ...current,
                    { id: nextToastId(), title: t('settings.toast.saved'), variant: 'success' },
                  ]);
                })();
              }}
            />
          )}
        </Framed>
      </ScreenState>

      <Toaster
        toasts={[...toasts]}
        label={t('settings.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}

/* ── the two sections that shipped before the shell existed ─────────────────────────────────── */

/**
 * ⚠️⚠️ **C10's and C5's screens, RE-PARENTED RATHER THAN REBUILT.** Both are sections of the mockup's
 * settings `NAV` and both shipped as standalone routes because no shell existed yet — which is
 * exactly the drift `app.tsx`'s header warns about, in the one place no lint could see it: a
 * hand-written rail beside a hand-written route table. Wrapping them means the rail cannot list a
 * section the router does not have, and the router cannot serve one the rail omits.
 *
 * ⚠️ Their own `data-screen` markers stay theirs (`metafields`, `permissions`), so the reachability
 * suite keeps asserting each address renders its own screen.
 */
export function SettingsMetafieldsRoute() {
  return (
    <Framed section="metafields">
      <MetafieldsRoute />
    </Framed>
  );
}

/* ── Projekt szerepek ───────────────────────────────────────────────────────────────────────── */

export function SettingsProjectRolesRoute() {
  const session = useSession();
  const t = useT();
  /* 26 Oct 2026 · the role dialog's success toast. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();
  const [roles, setRoles] = useState<readonly ProjectRole[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request('/project-roles').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    setRoles(parseList(projectRoleSchema, await response.json()) ?? []);
    setFailure(null);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /** 26 Oct 2026 · resolves to whether it landed — the role dialog closes on `true` only. */
  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) {
      const body = (await response?.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      /* ⚠️ Branching on the CODE — rule 5. `conflict` here is only ever the duplicate name. */
      setServerError(
        t(
          body?.error?.code === 'conflict'
            ? 'settings.projectRoles.error.duplicate'
            : 'error.unavailable',
        ),
      );
      return false;
    }
    await reload();
    return true;
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="project-roles">
        <ProjectRolesSection
          loading={loading}
          roles={roles}
          canManage={permissions.has('settings.manage')}
          serverError={serverError}
          onCreate={(body) =>
            confirmed(
              write('/project-roles', { method: 'POST', body: JSON.stringify(body) }),
              () => {
                saved(t('common.toast.created', { name: body.name }));
              },
            )
          }
          onUpdate={(id, body) =>
            confirmed(
              write(`/project-roles/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
              () => {
                saved(t('common.toast.saved', { name: body.name }));
              },
            )
          }
          onDelete={(id) => {
            void write(`/project-roles/${id}`, { method: 'DELETE' });
          }}
        />
      </Framed>
      {toaster}
    </ScreenState>
  );
}

/* ── Csapatok · R-60 ─────────────────────────────────────────────────────────────────────── */

/**
 * ⚠️ **Live, unlike project roles**: a team is renamed here while the people list, a dashboard and
 * its filter all name it — and the member counts move whenever somebody is put in one.
 */
export function SettingsTeamsRoute() {
  const session = useSession();
  const t = useT();
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();
  const [teams, setTeams] = useState<readonly Team[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request('/teams').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = parseList(teamSchema, await response.json());
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setTeams(parsed);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  const loading = useFirstLoad(reload);
  /* ⚠️ The same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(TEAM_TOPICS, reload);

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) {
      const body = (await response?.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      /* ⚠️ Branching on the CODE — rule 5. */
      setServerError(messageFor(t, body?.error?.code ?? 'unavailable'));
      return false;
    }
    await reload();
    return true;
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="teams">
        <TeamsSection
          loading={loading}
          teams={teams}
          canManage={permissions.has('settings.manage')}
          serverError={serverError}
          onCreate={(body) =>
            confirmed(write('/teams', { method: 'POST', body: JSON.stringify(body) }), () => {
              saved(t('common.toast.created', { name: body.name }));
            })
          }
          onUpdate={(id, body) =>
            confirmed(
              write(`/teams/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
              () => {
                saved(t('common.toast.saved', { name: body.name }));
              },
            )
          }
          onDelete={(id) => {
            void write(`/teams/${id}`, { method: 'DELETE' });
          }}
        />
      </Framed>
      {toaster}
    </ScreenState>
  );
}

export function SettingsPermissionsRoute() {
  return (
    <Framed section="permissions">
      <PermissionsRoute />
    </Framed>
  );
}

/* ── Integrációk ────────────────────────────────────────────────────────────────────────────── */

/**
 * C33 · ⚠️ **The section is now the integration CATALOGUE**, where C32 left a list of installs and
 * a *"hamarosan"* card. Three routes wear this shell; each brings its own screen, and the shell is
 * all they share.
 */
export function SettingsIntegrationsRoute() {
  return (
    <Framed section="integrations">
      <IntegrationsCatalogueRoute />
    </Framed>
  );
}

export function SettingsIntegrationProviderRoute() {
  return (
    <Framed section="integrations">
      <IntegrationProviderRoute />
    </Framed>
  );
}

export function SettingsIntegrationInstanceRoute() {
  return (
    <Framed section="integrations">
      <IntegrationInstanceRoute />
    </Framed>
  );
}

/* ── Auditnapló — ux-pass-2, U-8 ────────────────────────────────────────────────────────────── */

export function SettingsAuditRoute() {
  return (
    <Framed section="audit">
      <AuditSection />
    </Framed>
  );
}

/* ── Adatok ─────────────────────────────────────────────────────────────────────────────────── */

export function SettingsDataRoute() {
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();
  const { settings, denied, failure, reload } = useWorkspace();
  /* ⚠️ T-10's shared path — the card's four buttons open the one dialog rather than each holding
   * their own fetch. */
  const exporter = useExport();

  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setBusy(true);
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    setBusy(false);

    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      setServerError(messageFor(t, body?.error?.code ?? 'unavailable'));
      return false;
    }
    await reload();
    return true;
  };

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <Framed section="data">
          {denied ? (
            <SettingsDenied />
          ) : settings === null ? (
            <p className="text-muted-foreground p-5 text-[12.5px]">{t('state.loading')}</p>
          ) : (
            <DataSection
              settings={settings}
              canManage={permissions.has('settings.manage')}
              busy={busy}
              serverError={serverError}
              onRequestDeletion={(confirmName) => {
                void (async () => {
                  const ok = await write('/workspace/deletion', {
                    method: 'POST',
                    body: JSON.stringify({ confirmName, version: settings.version }),
                  });
                  if (ok) {
                    setToasts((current) => [
                      ...current,
                      {
                        id: nextToastId(),
                        title: t('settings.toast.deletionRequested'),
                        description: t('settings.toast.deletionGrace'),
                        variant: 'success',
                      },
                    ]);
                  }
                })();
              }}
              onExport={(domain) => {
                exporter.open(domain);
              }}
              onDeleteNow={(confirmName) => {
                void (async () => {
                  const ok = await write(
                    `/workspace?version=${String(settings.version)}&confirmName=${encodeURIComponent(confirmName)}`,
                    { method: 'DELETE' },
                  );
                  /* ⚠️ `0197` · the workspace is gone, and so is every address under it. The root
                     picks another membership or the welcome screen; nothing here can be reloaded. */
                  if (ok) window.location.assign('/');
                })();
              }}
              onCancelDeletion={() => {
                void (async () => {
                  const ok = await write(
                    `/workspace/deletion?version=${String(settings.version)}`,
                    { method: 'DELETE' },
                  );
                  if (ok) {
                    setToasts((current) => [
                      ...current,
                      {
                        id: nextToastId(),
                        title: t('settings.toast.deletionCancelled'),
                        variant: 'success',
                      },
                    ]);
                  }
                })();
              }}
            />
          )}
        </Framed>
      </ScreenState>

      {exporter.dialog}

      {/* ⚠️ One `Toaster`, both sources — two would stack in the same corner and hide each other. */}
      <Toaster
        toasts={[...toasts, ...exporter.toasts]}
        label={t('settings.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
          exporter.dismiss(id);
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}

/* ── Számlázás ──────────────────────────────────────────────────────────────────────────────── */

/**
 * ⚠️ **`billing.manage`, which is OWNER's alone** — so a 404 here is the denied body, not an error.
 * `settings.manage` is not enough: a company's tax number is not an operations setting.
 */
export function SettingsBillingRoute() {
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();

  const [details, setDetails] = useState<BillingDetails | null>(null);
  const [draft, setDraft] = useState<BillingDraft | null>(null);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session.request('/billing').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (response.status === 404) {
      setDenied(true);
      setFailure(null);
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = billingDetailsSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setDenied(false);
    setFailure(null);
    setDetails(parsed.data);
    setDraft(billingDraftOf(parsed.data));
  }, [session]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="billing">
        {denied ? (
          <SettingsDenied />
        ) : details === null || draft === null ? (
          <p className="text-muted-foreground p-5 text-[12.5px]">{t('state.loading')}</p>
        ) : (
          <BillingSection
            details={details}
            draft={draft}
            canManage={permissions.has('billing.manage')}
            saving={saving}
            serverError={serverError}
            onDraft={(patch) => {
              setDraft((current) => (current === null ? current : { ...current, ...patch }));
            }}
            onReset={() => {
              setDraft(billingDraftOf(details));
              setServerError(null);
            }}
            onSave={() => {
              void (async () => {
                setSaving(true);
                setServerError(null);
                const response = await session
                  .request('/billing', {
                    method: 'PATCH',
                    body: JSON.stringify({ ...draft, version: details.version }),
                  })
                  .catch(() => null);
                setSaving(false);

                if (response === null) {
                  setServerError(t('error.unavailable'));
                  return;
                }
                if (!response.ok) {
                  const body = (await response.json().catch(() => null)) as {
                    error?: { code?: string };
                  } | null;
                  const code = body?.error?.code ?? 'unavailable';
                  setServerError(
                    code === 'invalid_billing_field'
                      ? t('billing.error.rejected')
                      : messageFor(t, code),
                  );
                  return;
                }
                await reload();
              })();
            }}
          />
        )}
      </Framed>
    </ScreenState>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Ünnepnapok. C42, the workspace calendar.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The year is route state held here, not in the screen.** The screen filters what it is given;
 * the year decides what is FETCHED, and a control that changes a request belongs beside the request.
 *
 * ⚠️⚠️ **THE IMPORT RE-READS RATHER THAN MERGING ITS OWN ANSWER INTO THE LIST.** It returns three
 * counts and not the rows — deliberately, because what the calendar looks like afterwards is a fact
 * about the table (some of the person's own days survived, one of them may have blocked an imported
 * day) and reconstructing that on the client would be reimplementing the rule the server just
 * applied.
 */
export function SettingsHolidaysRoute() {
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();

  /* ⚠️ The CURRENT year from the browser, and three of them offered. The mockup hardcodes
     2025–2027, which is a fixture's convenience rather than a rule. */
  const thisYear = new Date().getFullYear();

  const [year, setYear] = useState(thisYear);
  const [holidays, setHolidays] = useState<readonly Holiday[]>([]);
  const [country, setCountry] = useState('HU');
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE YEARS THIS WORKSPACE HAS HAD — BACK TO ITS OWN FIRST ONE.        │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * Three windows in three days, and the third is the right one. It was last year, this year and
   * next — a window for READING a calendar. Then this year and the three after it, on the argument
   * that this screen BUILDS one and a past year offers an edit whose only effect is on history.
   * Corrected: *"it should also show past years assuming the tenant was live back then"* — a
   * workspace running since 2024 has real calendar behind it, and not being able to look at it is
   * the loss. The edit argument was answering a question nobody asked.
   *
   * ⚠️ **The workspace's own first year, from `createdAt`** — not a fixed number of years back. A
   * workspace opened this month offers this year and the three ahead, which is what it has.
   */
  const [openedIn, setOpenedIn] = useState(thisYear);
  const years = [];
  for (let one = Math.min(openedIn, thisYear); one <= thisYear + 3; one += 1) years.push(one);
  const [importing, setImporting] = useState(false);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  /* 26 Oct 2026 · the dialog's success toast, into this screen's own Toaster. */
  const saved = (title: string): void => {
    setToasts((current) => [
      ...current,
      { id: `saved-${String(Date.now())}`, title, variant: 'success' },
    ]);
  };

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session
      .request(`/workspace/holidays?year=${String(year)}`)
      .catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const parsed = parseList(holidaySchema, await response.json());
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setHolidays(parsed);

    /* ⚠️ **Loaded separately and its failure is NOT fatal** — the footer's *"Source: Nager.Date ·
       Hungary"* is a label, and losing it is a smaller loss than a screen that will not render. */
    const settings = await session.request('/workspace').catch(() => null);
    if (settings?.ok === true) {
      const body: unknown = await settings.json();
      const one = workspaceSettingsSchema.safeParse(body);
      if (one.success) {
        setCountry(one.data.country);
        /* ⚠️ The year the workspace opened — the first year the picker offers. See `years`. */
        setOpenedIn(new Date(one.data.createdAt).getFullYear());
      }
    }
  }, [session, year]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(CALENDAR_TOPICS, reload);

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      /* ⚠️ The one refusal with its own sentence: a day that already has an entry is something the
         person can act on, and `conflict` alone reads as a stale-version error. */
      setServerError(
        body?.error?.code === 'conflict'
          ? t('settings.holidays.error.taken')
          : messageFor(t, body?.error?.code ?? 'unavailable'),
      );
      return false;
    }
    await reload();
    return true;
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="holidays">
        <HolidaysSection
          loading={loading}
          year={year}
          years={years}
          holidays={holidays}
          countryLabel={countryName(t, country)}
          canManage={permissions.has('settings.manage')}
          importing={importing}
          serverError={serverError}
          onYear={setYear}
          onImport={() => {
            void (async () => {
              setImporting(true);
              setServerError(null);
              const response = await session
                .request('/workspace/holidays/import', {
                  method: 'POST',
                  body: JSON.stringify({ year }),
                })
                .catch(() => null);
              setImporting(false);

              if (response?.ok !== true) {
                setServerError(t('error.unavailable'));
                return;
              }
              const result = holidayImportResultSchema.safeParse(await response.json());
              await reload();
              if (result.success) {
                setToasts((current) => [
                  ...current,
                  {
                    id: `import-${String(Date.now())}`,
                    variant: 'success',
                    title: t('settings.nav.holidays'),
                    description: t('settings.holidays.imported', {
                      imported: result.data.imported,
                      country: result.data.country,
                      year: result.data.year,
                    }),
                  },
                ]);
              }
            })();
          }}
          onSave={(draft) => {
            return confirmed(
              write(
                draft.id === null
                  ? '/workspace/holidays'
                  : `/workspace/holidays/${encodeURIComponent(draft.id)}`,
                {
                  method: draft.id === null ? 'POST' : 'PUT',
                  body: JSON.stringify({
                    onDate: draft.onDate,
                    name: draft.name,
                    kind: draft.kind,
                    ...(draft.id === null ? {} : { version: draft.version }),
                  }),
                },
              ),
              () => {
                saved(
                  draft.id === null
                    ? t('common.toast.created', { name: draft.name })
                    : t('common.toast.saved', { name: draft.name }),
                );
              },
            );
          }}
          onDelete={(holiday) => {
            void write(`/workspace/holidays/${encodeURIComponent(holiday.id)}`, {
              method: 'DELETE',
            });
          }}
        />
      </Framed>
      <Toaster
        toasts={[...toasts]}
        label={t('settings.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </ScreenState>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Munkarendek. C43.                                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Three reads, and only the first two are fatal.** Templates and assignments are the screen;
 * the member list fills one dropdown, and losing it is a smaller loss than a screen that will not
 * render — the same trade the project screen's picker feeds make.
 */
export function SettingsSchedulesRoute() {
  /* R-60 · the team filter: the teams, and who is in which. */
  const teams = useTeams();
  const memberTeams = useMemberTeams();
  const session = useSession();
  const t = useT();
  /* 26 Oct 2026 · the dialogs' success toasts. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();
  /* ⚠️ R-38 · workspace-relative, so the first segment is supplied rather than typed. */
  const go = useWorkspaceNavigate();

  const [templates, setTemplates] = useState<readonly WorkScheduleTemplate[]>([]);
  const [assignments, setAssignments] = useState<readonly WorkScheduleAssignment[]>([]);
  const [members, setMembers] = useState<
    readonly { id: string; name: string; avatarSeed: string | null; active: boolean }[]
  >([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const [templateResponse, assignmentResponse] = await Promise.all([
      session.request('/workspace/schedules/templates').catch(() => null),
      session.request('/workspace/schedules/assignments').catch(() => null),
    ]);
    if (templateResponse === null || assignmentResponse === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!templateResponse.ok) {
      setFailure(await failureOf(templateResponse));
      return;
    }
    if (!assignmentResponse.ok) {
      setFailure(await failureOf(assignmentResponse));
      return;
    }

    const parsedTemplates = parseList(workScheduleTemplateSchema, await templateResponse.json());
    const parsedAssignments = parseList(
      workScheduleAssignmentSchema,
      await assignmentResponse.json(),
    );
    if (parsedTemplates === null || parsedAssignments === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setTemplates(parsedTemplates);
    setAssignments(parsedAssignments);

    const people = await requestAll(session, '/people').catch(() => null);
    if (people?.ok === true) {
      const parsed = parseList(personSchema, await people.json());
      if (parsed !== null) {
        setMembers(
          parsed.map((person) => ({
            id: person.id,
            name: person.displayName,
            avatarSeed: person.avatarSeed,
            /* R2 · listed, but not offered for a new assignment. */
            active: person.status === 'active',
          })),
        );
      }
    }
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(SCHEDULE_TOPICS, reload);

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { field?: string }[] };
      } | null;
      /* ⚠️ The in-use refusal gets its own sentence, because *deactivate instead* is the action and
         a bare `conflict` reads as a stale-version error. */
      setServerError(
        body?.error?.details?.[0]?.field === 'assignedCount'
          ? t('settings.schedules.error.inUse')
          : messageFor(t, body?.error?.code ?? 'unavailable'),
      );
      return false;
    }
    await reload();
    return true;
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="schedules">
        <SchedulesSection
          teams={teams}
          memberTeams={memberTeams}
          loading={loading}
          templates={templates}
          assignments={assignments}
          members={members}
          canManage={permissions.has('settings.manage')}
          serverError={serverError}
          /* ⚠️ Only for a reader who may open a person — the menu entry is absent otherwise, rather
             than present and landing on a refusal. */
          onOpenPerson={
            permissions.has('people.view')
              ? (memberId) => {
                  go(`/people/${memberId}`);
                }
              : undefined
          }
          onSaveTemplate={(draft) => {
            return confirmed(
              write(
                draft.id === null
                  ? '/workspace/schedules/templates'
                  : `/workspace/schedules/templates/${encodeURIComponent(draft.id)}`,
                {
                  method: draft.id === null ? 'POST' : 'PUT',
                  body: JSON.stringify({
                    name: draft.name,
                    type: draft.type,
                    days: draft.days,
                    weeklyHours: draft.weeklyHours,
                    active: draft.active,
                    ...(draft.id === null ? {} : { version: draft.version }),
                  }),
                },
              ),
              () => {
                saved(
                  draft.id === null
                    ? t('common.toast.created', { name: draft.name })
                    : t('common.toast.saved', { name: draft.name }),
                );
              },
            );
          }}
          onDeleteTemplate={(template) => {
            void write(`/workspace/schedules/templates/${encodeURIComponent(template.id)}`, {
              method: 'DELETE',
            });
          }}
          onSaveAssignment={(draft) => {
            return confirmed(
              write('/workspace/schedules/assignments', {
                method: 'POST',
                body: JSON.stringify({
                  memberId: draft.memberId,
                  fromDate: draft.fromDate,
                  /* ⚠️ Either arm, never both — the wire schema refuses a body carrying a template
                   id AND seven hours, so the two are spread conditionally rather than nulled. */
                  ...(draft.templateId === null
                    ? { type: draft.type, days: draft.days, weeklyHours: draft.weeklyHours }
                    : { templateId: draft.templateId }),
                  ...(draft.version === null ? {} : { version: draft.version }),
                }),
              }),
              () => {
                saved(t('common.toast.savedPlain'));
              },
            );
          }}
          onDeleteAssignment={(assignment) => {
            void write(`/workspace/schedules/assignments/${encodeURIComponent(assignment.id)}`, {
              method: 'DELETE',
            });
          }}
        />
      </Framed>
      {toaster}
    </ScreenState>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Szabadságkezelés. C46.                                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The people list is fetched here rather than derived from the quotas**, because the grid has
 * to draw a row for somebody who has no entitlement yet — that empty row IS the affordance for
 * giving them one.
 *
 * ⚠️⚠️ **THE EXCLUDED NAMES COME FROM THE SAME READ.** `leaveManaged` is a fact about a person, so
 * the grid's footnote is the people list filtered rather than a second endpoint answering a
 * question the first one already did.
 */
export function SettingsLeaveRoute() {
  /* R-60 · the team filter: the teams, and who is in which. */
  const teams = useTeams();
  const memberTeams = useMemberTeams();
  const session = useSession();
  const t = useT();
  /* 26 Oct 2026 · the dialogs' success toasts. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();

  const thisYear = new Date().getFullYear();
  /**
   * ⚠️⚠️ **THIS YEAR AND THE THREE AFTER IT — 15 Sep.** It was last year, this year and next, which
   * is a window for READING a calendar and this screen is for building one: a workspace sets its
   * days off ahead, and the year that mattered least is the one that has already happened. Naming a
   * past year here also offered an edit whose only effect would be on history.
   */
  const years = [thisYear, thisYear + 1, thisYear + 2, thisYear + 3];

  const [year, setYear] = useState(thisYear);
  const [types, setTypes] = useState<readonly LeaveType[]>([]);
  const [quotas, setQuotas] = useState<readonly LeaveQuota[]>([]);
  const [people, setPeople] = useState<readonly Person[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const typeResponse = await session.request('/workspace/leave/types').catch(() => null);
    if (typeResponse === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!typeResponse.ok) {
      setFailure(await failureOf(typeResponse));
      return;
    }

    const parsedTypes = parseList(leaveTypeSchema, await typeResponse.json());
    if (parsedTypes === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setTypes(parsedTypes);

    /* ⚠️ Not fatal: losing it costs the grid its rows, not the screen. */
    const peopleList = await requestAll(session, '/people').catch(() => null);
    if (peopleList?.ok === true) {
      const parsed = parseList(personSchema, await peopleList.json());
      if (parsed !== null) setPeople(parsed);
    }
  }, [session]);

  /**
   * ⚠️ **A second read, keyed on the year.** It is deliberately NOT folded into `reload`: the types
   * and the people do not change with the year, and re-reading them would cost three requests.
   *
   * ⚠️⚠️ **THE WHOLE YEAR, AND IT WAS NOTHING AT ALL (fixed 13 Oct 2026).** This read waited for the
   * grid to report which people were on its page (`?memberIds=`), from when the grid paged people.
   * The flat quota list that replaced it pages ENTITLEMENTS and never reported anybody, so the read
   * never ran and *Keretek* was empty for everybody. Without `memberIds` the endpoint answers for
   * the whole workspace — and the spent-days figure each quota carries is two queries now rather
   * than a walk per day (`workingDayCounts`), which was the cost the paging existed to cap.
   */
  const reloadQuotas = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    const response = await session
      .request(`/workspace/leave/quotas?year=${String(year)}`)
      .catch(() => null);
    if (response?.ok !== true) return;
    const parsed = parseList(leaveQuotaSchema, await response.json());
    if (parsed !== null) setQuotas(parsed);
  }, [session, year]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ Its own first load: the quota table waits for THIS answer, not for the types'. */
  const quotasLoading = useFirstLoad(reloadQuotas);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. Both reads, because a
     decision elsewhere changes what is spent as surely as an edit here changes the entitlement. */
  const refreshAll = useCallback(async (): Promise<void> => {
    await reload();
    await reloadQuotas();
  }, [reload, reloadQuotas]);
  useLiveInvalidation(LEAVE_TOPICS, refreshAll);

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { field?: string }[] };
      } | null;
      const field = body?.error?.details?.[0]?.field;
      setServerError(
        field === 'code'
          ? t('settings.leave.error.taken')
          : field === 'memberId'
            ? t('settings.leave.error.notManaged')
            : messageFor(t, body?.error?.code ?? 'unavailable'),
      );
      return false;
    }
    /* ⚠️ Both: saving a quota changes the grid, saving a TYPE changes which columns it has. */
    await reload();
    await reloadQuotas();
    return true;
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="leave">
        <LeaveSection
          teams={teams}
          memberTeams={memberTeams}
          loading={loading}
          quotasLoading={loading || quotasLoading}
          types={types}
          quotas={quotas}
          /* ⚠️ Only the people this workspace manages leave for get a row; the rest are named in
             the footnote instead, which is what makes their absence read as a decision. */
          /**
           * ⚠️⚠️ **ACTIVE ONLY, AND AN INVITATION IS NOT A PERSON YET.** An invited colleague showed
           * up as a row headed by their email address with two empty steppers under it — an
           * entitlement for somebody who has not accepted, and whose name we do not know. They join
           * the grid the moment they sign in. A DEACTIVATED member is out for the opposite reason:
           * their entitlement is history, not something to edit.
           */
          people={people
            .filter(
              (person) =>
                person.leaveManaged && person.deletedAt === null && person.status === 'active',
            )
            .map((person) => ({
              id: person.id,
              name: person.displayName,
              avatarSeed: person.avatarSeed,
            }))}
          excluded={people
            .filter(
              (person) =>
                !person.leaveManaged && person.deletedAt === null && person.status === 'active',
            )
            .map((person) => person.displayName)}
          year={year}
          years={years}
          canManage={permissions.has('settings.manage')}
          serverError={serverError}
          onYear={setYear}
          onSaveType={(draft) => {
            return confirmed(
              write(
                draft.id === null
                  ? '/workspace/leave/types'
                  : `/workspace/leave/types/${encodeURIComponent(draft.id)}`,
                {
                  method: draft.id === null ? 'POST' : 'PUT',
                  body: JSON.stringify({
                    name: draft.name,
                    icon: draft.icon,
                    active: draft.active,
                    backdate: draft.backdate,
                    quotaSource: draft.quotaSource,
                    carryOver: draft.carryOver,
                    carryToMonth: draft.carryToMonth,
                    noteRule: draft.noteRule,
                    attachmentRule: draft.attachmentRule,
                    ...(draft.id === null ? {} : { version: draft.version }),
                  }),
                },
              ),
              () => {
                saved(
                  draft.id === null
                    ? t('common.toast.created', { name: draft.name })
                    : t('common.toast.saved', { name: draft.name }),
                );
              },
            );
          }}
          onDeleteType={(type) => {
            void write(`/workspace/leave/types/${encodeURIComponent(type.id)}`, {
              method: 'DELETE',
            });
          }}
          /* ⚠️ The whole set in its new order — the server refuses anything that is not a
             permutation of what it holds, which is what makes a stale drag a refusal. */
          onReorderTypes={
            permissions.has('settings.manage')
              ? (ids) => {
                  void write('/workspace/leave/types/order', {
                    method: 'PUT',
                    body: JSON.stringify({ ids }),
                  });
                }
              : undefined
          }
          onSaveQuota={(memberId, leaveTypeId, days) => {
            void write('/workspace/leave/quotas', {
              method: 'PUT',
              body: JSON.stringify({ memberId, leaveTypeId, year, days }),
            });
          }}
          {...(permissions.has('settings.manage')
            ? {
                onCopyQuotas: (memberIds: readonly string[]) => {
                  void write('/workspace/leave/quotas/copy', {
                    method: 'POST',
                    body: JSON.stringify({ year, memberIds }),
                  });
                },
              }
            : {})}
        />
      </Framed>
      {toaster}
    </ScreenState>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Túlóra. C48.                                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 */
/**
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Skill menedzsment. C54.                                  │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THREE READS IN ONE PASS, AND THEY CANNOT BE SPLIT PER TAB.** The skill rows draw
 * their category's name and colour, the tab strip shows all three counts before anything is
 * opened, and the create dialog needs the category list to be fillable at all. Loading a tab's
 * list when it is selected would make the counts wrong until somebody clicked.
 *
 * ⚠️ One `Promise.all`, and any one of the three failing fails the screen — a skills list with
 * no categories behind it would render every row with a blank dot and no explanation.
 */
export function SettingsSkillsRoute() {
  const session = useSession();
  const t = useT();
  /* 26 Oct 2026 · the dialogs' success toasts. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();

  const [skills, setSkills] = useState<readonly Skill[]>([]);
  const [categories, setCategories] = useState<readonly SkillCategory[]>([]);
  const [levels, setLevels] = useState<readonly SkillLevel[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const [skillsResponse, categoriesResponse, levelsResponse] = await Promise.all([
      session.request('/skills').catch(() => null),
      session.request('/skills/categories').catch(() => null),
      session.request('/skills/levels').catch(() => null),
    ]);
    if (skillsResponse === null || categoriesResponse === null || levelsResponse === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    for (const response of [skillsResponse, categoriesResponse, levelsResponse]) {
      if (!response.ok) {
        setFailure(await failureOf(response));
        return;
      }
    }
    const parsedSkills = parseList(skillSchema, await skillsResponse.json());
    const parsedCategories = parseList(skillCategorySchema, await categoriesResponse.json());
    const parsedLevels = parseList(skillLevelSchema, await levelsResponse.json());
    if (parsedSkills === null || parsedCategories === null || parsedLevels === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setSkills(parsedSkills);
    setCategories(parsedCategories);
    setLevels(parsedLevels);
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(SKILL_TOPICS, reload);

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      setServerError(messageFor(t, body?.error?.code ?? 'unavailable'));
      return false;
    }
    await reload();
    return true;
  };

  const canManage = permissions.has('settings.manage');

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="skills">
        <SkillsSection
          loading={loading}
          skills={skills}
          categories={categories}
          levels={levels}
          canManage={canManage}
          serverError={serverError}
          onSaveSkill={(draft) => {
            return confirmed(
              write(draft.id === null ? '/skills' : `/skills/${encodeURIComponent(draft.id)}`, {
                method: draft.id === null ? 'POST' : 'PUT',
                body: JSON.stringify({
                  name: draft.name,
                  categoryId: draft.categoryId,
                  description: draft.description.trim() === '' ? null : draft.description,
                  ...(draft.id === null ? {} : { version: draft.version }),
                }),
              }),
              () => {
                saved(
                  draft.id === null
                    ? t('common.toast.created', { name: draft.name })
                    : t('common.toast.saved', { name: draft.name }),
                );
              },
            );
          }}
          onDeleteSkill={(skill) => {
            void write(`/skills/${encodeURIComponent(skill.id)}`, { method: 'DELETE' });
          }}
          onReorderSkills={
            canManage
              ? (ids) => {
                  void write('/skills/order', { method: 'PUT', body: JSON.stringify({ ids }) });
                }
              : undefined
          }
          onSaveCategory={(draft) => {
            return confirmed(
              write(
                draft.id === null
                  ? '/skills/categories'
                  : `/skills/categories/${encodeURIComponent(draft.id)}`,
                {
                  method: draft.id === null ? 'POST' : 'PUT',
                  body: JSON.stringify({
                    name: draft.name,
                    color: draft.color,
                    ...(draft.id === null ? {} : { version: draft.version }),
                  }),
                },
              ),
              () => {
                saved(
                  draft.id === null
                    ? t('common.toast.created', { name: draft.name })
                    : t('common.toast.saved', { name: draft.name }),
                );
              },
            );
          }}
          onDeleteCategory={(category) => {
            void write(`/skills/categories/${encodeURIComponent(category.id)}`, {
              method: 'DELETE',
            });
          }}
          onReorderCategories={
            canManage
              ? (ids) => {
                  void write('/skills/categories/order', {
                    method: 'PUT',
                    body: JSON.stringify({ ids }),
                  });
                }
              : undefined
          }
          onSaveLevel={(draft) => {
            return confirmed(
              write(
                draft.id === null
                  ? '/skills/levels'
                  : `/skills/levels/${encodeURIComponent(draft.id)}`,
                {
                  method: draft.id === null ? 'POST' : 'PUT',
                  body: JSON.stringify({
                    name: draft.name,
                    color: draft.color,
                    description: draft.description.trim() === '' ? null : draft.description,
                    ...(draft.id === null ? {} : { version: draft.version }),
                  }),
                },
              ),
              () => {
                saved(
                  draft.id === null
                    ? t('common.toast.created', { name: draft.name })
                    : t('common.toast.saved', { name: draft.name }),
                );
              },
            );
          }}
          onDeleteLevel={(level) => {
            void write(`/skills/levels/${encodeURIComponent(level.id)}`, { method: 'DELETE' });
          }}
          /* ⚠️⚠️ **THIS ONE RE-WEIGHTS THE SCALE**, which is why it is not the same gesture
             as the two above it however identical the call looks. See the section's own note. */
          onReorderLevels={
            canManage
              ? (ids) => {
                  void write('/skills/levels/order', {
                    method: 'PUT',
                    body: JSON.stringify({ ids }),
                  });
                }
              : undefined
          }
        />
      </Framed>
      {toaster}
    </ScreenState>
  );
}

/** Beállítások → Túlóra. C48. */
export function SettingsOvertimeRoute() {
  const session = useSession();
  const t = useT();
  /* 26 Oct 2026 · the dialogs' success toasts. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();

  const [types, setTypes] = useState<readonly OvertimeType[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request('/overtime/types').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = parseList(overtimeTypeSchema, await response.json());
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setTypes(parsed);
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(OVERTIME_TOPICS, reload);

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string };
      } | null;
      /* ⚠️ `code_taken` went with `0145` — an overtime type has no code to take, so there is
         nothing left for this writer to say that `messageFor` does not. */
      setServerError(messageFor(t, body?.error?.code ?? 'unavailable'));
      return false;
    }
    await reload();
    return true;
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="overtime">
        <OvertimeSection
          loading={loading}
          types={types}
          canManage={permissions.has('settings.manage')}
          serverError={serverError}
          onSave={(draft) => {
            return confirmed(
              write(
                draft.id === null
                  ? '/overtime/types'
                  : `/overtime/types/${encodeURIComponent(draft.id)}`,
                {
                  method: draft.id === null ? 'POST' : 'PUT',
                  body: JSON.stringify({
                    name: draft.name,
                    multiplier: draft.multiplier,
                    appliesWhen: draft.appliesWhen,
                    active: draft.active,
                    isDefault: draft.isDefault,
                    ...(draft.id === null ? {} : { version: draft.version }),
                  }),
                },
              ),
              () => {
                saved(
                  draft.id === null
                    ? t('common.toast.created', { name: draft.name })
                    : t('common.toast.saved', { name: draft.name }),
                );
              },
            );
          }}
          onDelete={(type) => {
            void write(`/overtime/types/${encodeURIComponent(type.id)}`, { method: 'DELETE' });
          }}
          /* ⚠️ The whole set in its new order — the server refuses anything that is not a
             permutation of what it holds, which is what makes a stale drag a refusal. */
          onReorder={
            permissions.has('settings.manage')
              ? (ids) => {
                  void write('/overtime/types/order', {
                    method: 'PUT',
                    body: JSON.stringify({ ids }),
                  });
                }
              : undefined
          }
        />
      </Framed>
      {toaster}
    </ScreenState>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Pénznemek. C51.                                             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Two reads**: the currency list (tab 1) and one page of the daily table (tab 2,
 * FX-1 … FX-8). A currency write re-reads the list; a day's rate is written optimistically into the
 * page and reverted on refusal (`state-rules.md` §7), then both are re-read — a hand-typed rate on
 * the newest day moves tab 1's *Mai árfolyam*, and the cell's `version` moved with the write.
 */
/** What the currency card is drawn over until the first answer — skeleton rows, not a list. */
const NO_CURRENCIES: CurrencySettings = {
  currencies: [],
  source: 'manual',
  syncedAt: null,
  endpoint: '',
};

/** One page of *Napi árfolyamok*, as the route holds it. */
interface RatePage {
  readonly days: readonly CurrencyRateDay[];
  readonly codes: readonly string[];
}

/** The daily table opens newest first — the server's own default. */
const RATES_DEFAULT_SORT: SortState = { key: 'date', direction: 'desc' };

/** The refusal envelope, read loosely: clients branch on `code` and on a detail's `field`/`code`. */
async function refusalOf(
  response: Response,
): Promise<{ code: string; field?: string; detail?: string }> {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; details?: { field?: string; code?: string }[] };
  } | null;
  const first = body?.error?.details?.[0];
  return {
    code: body?.error?.code ?? 'unavailable',
    ...(first?.field === undefined ? {} : { field: first.field }),
    ...(first?.code === undefined ? {} : { detail: first.code }),
  };
}

export function SettingsCurrencyRoute() {
  const session = useSession();
  const { t, locale } = useI18n();
  const permissions = usePermissionSet();

  const [settings, setSettings] = useState<CurrencySettings | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  /* 26 Oct 2026 · the dialog's success toast, into this screen's own Toaster. */
  const saved = (title: string): void => {
    setToasts((current) => [
      ...current,
      { id: `saved-${String(Date.now())}`, title, variant: 'success' },
    ]);
  };
  const toast = (entry: Omit<Toast, 'id'>): void => {
    setToasts((current) => [...current, { ...entry, id: nextToastId() }]);
  };

  /* ── tab 2 · one page of daily rates, sorted and paged on the server ── */
  const pages = usePages(50);
  const { settle, reset, offset, limit } = pages;
  const [rateSort, setRateSort] = useState<SortState>(RATES_DEFAULT_SORT);
  const [rates, setRates] = useState<RatePage>({ days: [], codes: [] });
  const [ratesFailed, setRatesFailed] = useState(false);

  /**
   * ⚠️ **Fetch, then swap — never clear first** (`state-rules.md` §11): a live refetch or a page
   * turn re-reads behind the rows already shown, and a table that blanked would flash.
   */
  const reloadRates = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    const query = new URLSearchParams({
      /* ⚠️ A sort key is lower case on the wire (`parseSort`'s grammar), so `EUR` travels as
         `eur`; the service matches it back against the currency codes. */
      sort: formatSort([{ ...rateSort, key: rateSort.key.toLowerCase() }]) ?? 'date:desc',
      limit: String(limit),
      offset: String(offset),
    });
    const response = await session
      .request(`/workspace/currencies/rates?${query.toString()}`)
      .catch(() => null);
    if (!response?.ok) {
      setRatesFailed(true);
      return;
    }
    const body = (await response.json().catch(() => null)) as {
      codes?: unknown;
      meta?: { total?: unknown };
    } | null;
    const days = parseList(currencyRateDaySchema, body);
    const codes = body?.codes;
    const total = body?.meta?.total;
    if (
      days === null ||
      !Array.isArray(codes) ||
      !codes.every((one): one is string => typeof one === 'string') ||
      typeof total !== 'number'
    ) {
      setRatesFailed(true);
      return;
    }
    setRatesFailed(false);
    setRates({ days, codes });
    settle(total);
  }, [session, rateSort, limit, offset, settle]);

  const ratesLoading = useFirstLoad(reloadRates);

  /** Replace one cell in the page on screen — the optimistic half of a write, and its revert. */
  const patchCell = (date: string, code: string, next: CurrencyRateCell): void => {
    setRates((current) => ({
      ...current,
      days: current.days.map((day) =>
        day.date === date ? { ...day, rates: { ...day.rates, [code]: next } } : day,
      ),
    }));
  };
  const cellOf = (date: string, code: string): CurrencyRateCell | null =>
    rates.days.find((day) => day.date === date)?.rates[code] ?? null;

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session.request('/workspace/currencies').catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = currencySettingsSchema.safeParse(await response.json());
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setSettings(parsed.data);
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /**
   * ⚠️ **Both tabs are live on the money topics.** Every `currency.*` audit action — the new
   * `currency.rate_set`, a sync, the daily chore — invalidates `currency`, and a rate typed by a
   * colleague has to reach this table as surely as a renamed currency reaches the list. An open
   * draft in a cell survives the refetch: it is the cell's own state (`state-rules.md` §11).
   */
  const reloadBoth = useCallback(async (): Promise<void> => {
    await Promise.all([reload(), reloadRates()]);
  }, [reload, reloadRates]);
  useLiveInvalidation(MONEY_TOPICS, reloadBoth);

  /**
   * One day's rate: written into the page at once, then sent; a refusal puts the old cell back and
   * says why in the section's error slot. ⚠️ `undo` is the reverse mutation (`state-rules.md` §7)
   * and can fail — a cell that was the bank's goes back by DELETE, a hand-typed one by PUT.
   */
  const writeRate = async (
    date: string,
    code: string,
    next: { kind: 'set'; value: number } | { kind: 'restore' },
  ): Promise<void> => {
    const before = cellOf(date, code);
    if (before === null) return;
    const dateLabel = formatCalendarDate(date, locale);
    const path = `/workspace/currencies/rates/${date}/${code}`;

    setServerError(null);
    patchCell(
      date,
      code,
      next.kind === 'set'
        ? { ...before, value: String(next.value), source: 'manual' }
        : { ...before, value: before.bankValue ?? before.value, source: 'ecb' },
    );
    const response = await session
      .request(
        path,
        next.kind === 'set'
          ? {
              method: 'PUT',
              body: JSON.stringify({ value: next.value, version: before.version }),
            }
          : { method: 'DELETE' },
      )
      .catch(() => null);

    if (!response?.ok) {
      patchCell(date, code, before);
      const refusal = response === null ? { code: 'unavailable' } : await refusalOf(response);
      if ('detail' in refusal && refusal.detail === 'not_a_banking_day') {
        setServerError(t('fx.error.notBankingDay', { code, date: dateLabel }));
      } else if ('field' in refusal && refusal.field === 'version') {
        setServerError(t('fx.error.stale', { code, date: dateLabel }));
        await reloadRates();
      } else {
        setServerError(messageFor(t, refusal.code));
      }
      return;
    }

    await reloadBoth();
    toast({
      title:
        next.kind === 'set'
          ? t('fx.toast.saved', { code, date: dateLabel })
          : t('fx.toast.restored', { code, date: dateLabel }),
      variant: 'success',
      action: {
        label: t('fx.toast.undo'),
        failureLabel: t('fx.toast.undoFailed'),
        onAction: async () => {
          /* ⚠️ No `version`: the write above moved it and the page has re-read; the undo is the
             same person taking back what they just did, seconds later. */
          const undo = await session
            .request(
              path,
              before.source === 'ecb' && before.bankValue !== null
                ? { method: 'DELETE' }
                : { method: 'PUT', body: JSON.stringify({ value: Number(before.value) }) },
            )
            .catch(() => null);
          if (!undo?.ok) throw new Error('undo refused');
          await reloadBoth();
        },
      },
    });
  };

  /**
   * *Lekérdezés most*. ⚠️ A 503 `unavailable` means the BANK did not answer, not us — the toast
   * says so, because the reader's next move is *try later*, not *report a bug*.
   */
  const sync = async (): Promise<void> => {
    setSyncing(true);
    setServerError(null);
    const response = await session
      .request('/workspace/currencies/sync', { method: 'POST' })
      .catch(() => null);
    setSyncing(false);
    if (!response?.ok) {
      const refusal = response === null ? { code: 'unavailable' } : await refusalOf(response);
      toast({
        title: t('fx.toast.syncFailed'),
        description:
          refusal.code === 'unavailable' || response?.status === 503
            ? t('fx.toast.bankDown')
            : messageFor(t, refusal.code),
        variant: 'error',
      });
      return;
    }
    const parsed = currencySettingsSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) setSettings(parsed.data);
    await reloadBoth();
    toast({ title: t('fx.toast.synced'), variant: 'success' });
  };

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { code?: string }[] };
      } | null;
      /* ⚠️ Each refusal keeps its own sentence — `money.controller.ts` argues why they are three
         codes rather than one `conflict`, and this is the half that makes that worth doing. */
      const detail = body?.error?.details?.[0]?.code;
      setServerError(
        detail === 'currency_in_use'
          ? t('settings.currency.error.inUse')
          : detail === 'currency_is_default'
            ? t('settings.currency.error.isDefault')
            : messageFor(t, body?.error?.code ?? 'unavailable'),
      );
      return false;
    }
    /* ⚠️ The daily table re-reads too: a currency added, retired or made the base changes its
       columns, and a new base re-prices every day (FX-7). */
    await reloadBoth();
    return true;
  };

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <Framed section="currency">
          {settings === null && !loading ? null : (
            <CurrencySection
              loading={loading}
              settings={settings ?? NO_CURRENCIES}
              canManage={permissions.has('settings.manage')}
              syncing={syncing}
              serverError={serverError}
              daily={{
                loading: ratesLoading,
                failed: ratesFailed,
                days: rates.days,
                codes: rates.codes,
                sort: rateSort,
                onSort: (next) => {
                  setRateSort(next);
                  /* ⚠️ A new order is a new set to page through — back to page one (§6). */
                  reset();
                },
                page: {
                  hasPrevious: pages.hasPrevious,
                  hasNext: pages.hasNext,
                  onPrevious: pages.previous,
                  onNext: pages.next,
                  index: pages.index,
                  count: pages.count,
                  total: pages.total,
                  onGoTo: pages.goTo,
                  pageSize: pages.limit,
                },
                onSet: (date, code, value) => {
                  void writeRate(date, code, { kind: 'set', value });
                },
                onRestore: (date, code) => {
                  void writeRate(date, code, { kind: 'restore' });
                },
                onRetry: () => {
                  void reloadRates();
                },
              }}
              onCreate={(draft) => {
                return confirmed(
                  write('/workspace/currencies', {
                    method: 'POST',
                    body: JSON.stringify(draft),
                  }),
                  () => {
                    saved(t('common.toast.created', { name: draft.code }));
                  },
                );
              }}
              onUpdate={(currency, patch) => {
                void write(`/workspace/currencies/${currency.code}`, {
                  method: 'PUT',
                  body: JSON.stringify({ ...patch, version: currency.version }),
                });
              }}
              onSetDefault={(code) => {
                void write('/workspace/currencies/default', {
                  method: 'POST',
                  body: JSON.stringify({ code }),
                });
              }}
              onDelete={(currency) => {
                void write(`/workspace/currencies/${currency.code}`, { method: 'DELETE' });
              }}
              onSync={() => {
                void sync();
              }}
            />
          )}
        </Framed>
      </ScreenState>

      <Toaster
        toasts={[...toasts]}
        label={t('settings.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Díjkártyák. C51.                                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Three reads, because the screen draws three things**: the cards, who is on them, and the
 * currencies a period can be priced in. The people list is the fourth and is not fatal — a picker
 * with no options is a worse screen than one that will not render, but only just.
 */
export function SettingsRatesRoute() {
  /* R-60 · the team filter: the teams, and who is in which. */
  const teams = useTeams();
  const memberTeams = useMemberTeams();
  const session = useSession();
  const t = useT();
  /* 26 Oct 2026 · the dialogs' success toasts. */
  const { saved, toaster } = useSaveToasts();
  const permissions = usePermissionSet();

  const [templates, setTemplates] = useState<readonly RateCardTemplate[]>([]);
  const [assignments, setAssignments] = useState<RateAssignmentList>({
    assignments: [],
    missing: [],
  });
  const [currencies, setCurrencies] = useState<readonly WorkspaceCurrency[]>([]);
  const [people, setPeople] = useState<readonly { id: string; name: string; active: boolean }[]>(
    [],
  );
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const [cardResponse, assignResponse, currencyResponse] = await Promise.all([
      session.request('/workspace/rate-cards').catch(() => null),
      session.request('/workspace/rate-cards/assignments/all').catch(() => null),
      session.request('/workspace/currencies').catch(() => null),
    ]);
    if (cardResponse === null || assignResponse === null || currencyResponse === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!cardResponse.ok) {
      setFailure(await failureOf(cardResponse));
      return;
    }

    const parsedCards = parseList(rateCardTemplateSchema, await cardResponse.json());
    const parsedAssignments = rateAssignmentListSchema.safeParse(await assignResponse.json());
    const parsedCurrencies = currencySettingsSchema.safeParse(await currencyResponse.json());
    if (parsedCards === null || !parsedAssignments.success || !parsedCurrencies.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setTemplates(parsedCards);
    setAssignments(parsedAssignments.data);
    setCurrencies(parsedCurrencies.data.currencies);

    const peopleResponse = await requestAll(session, '/people').catch(() => null);
    if (peopleResponse?.ok === true) {
      const parsed = parseList(personSchema, await peopleResponse.json());
      if (parsed !== null) {
        setPeople(
          parsed.map((person) => ({
            id: person.id,
            name: person.displayName,
            /* R2 · not offered for a new rate assignment. */
            active: person.status === 'active',
          })),
        );
      }
    }
  }, [session]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  useLiveInvalidation(MONEY_TOPICS, reload);

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { code?: string; message?: string }[] };
      } | null;
      const detail = body?.error?.details?.[0];
      setServerError(
        detail?.code === 'ratecard_in_use'
          ? t('settings.rates.error.inUse', { count: Number(detail.message ?? '0') })
          : messageFor(t, body?.error?.code ?? 'unavailable'),
      );
      return false;
    }
    await reload();
    return true;
  };

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <Framed section="rates">
        <RateCardSection
          teams={teams}
          memberTeams={memberTeams}
          loading={loading}
          templates={templates}
          assignments={assignments}
          currencies={currencies}
          people={people}
          canManage={permissions.has('settings.manage')}
          serverError={serverError}
          onSaveTemplate={(id, body) => {
            return confirmed(
              write(id === null ? '/workspace/rate-cards' : `/workspace/rate-cards/${id}`, {
                method: id === null ? 'POST' : 'PUT',
                body: JSON.stringify(body),
              }),
              () => {
                saved(
                  id === null
                    ? t('common.toast.created', { name: body.name })
                    : t('common.toast.saved', { name: body.name }),
                );
              },
            );
          }}
          onDeleteTemplate={(template) => {
            void write(`/workspace/rate-cards/${template.id}`, { method: 'DELETE' });
          }}
          onSaveAssignment={(id, body) => {
            return confirmed(
              write(
                id === null
                  ? '/workspace/rate-cards/assignments'
                  : `/workspace/rate-cards/assignments/${id}`,
                { method: id === null ? 'POST' : 'PUT', body: JSON.stringify(body) },
              ),
              () => {
                saved(t('common.toast.savedPlain'));
              },
            );
          }}
          onDeleteAssignment={(assignment) => {
            void write(`/workspace/rate-cards/assignments/${assignment.id}`, { method: 'DELETE' });
          }}
        />
      </Framed>
      {toaster}
    </ScreenState>
  );
}
